import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarCheck,
  LayoutGrid,
  List as ListIcon,
  SlidersHorizontal,
  User,
  Users,
  X,
} from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Input } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Drawer } from '@/components/ui/Drawer';
import { PipelineStageBadge } from '@/components/ui/StatusBadge';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores/auth';
import { displayName } from '@shared/prospect';
import {
  opportunitiesApi,
  ORIGIN_ORDER,
  ORIGIN_VISUAL,
  type LossReason,
  type OpportunitiesSummary,
  type OpportunityListItem,
  type OpportunityOrigin,
  type OpportunityStage,
  PIPELINE_STAGES,
  STAGE_LABELS,
  STAGE_VISUAL,
} from '@/services/opportunities.api';
import { configApi, type ContractType, type InsuranceCompany } from '@/services/config.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { apporteursApi, apporteurDisplayName, type ApporteurType } from '@/services/apporteurs.api';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { cn } from '@/lib/utils';
import { useRealtimeEvent, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import { DateRangeBadges } from '@/components/ui/DateRangePicker';
import { PERIOD_PRESETS, detectPeriodPresetDateOnly } from '@/lib/date-presets';
import { Tooltip } from '@/components/ui/Tooltip';

type View = 'kanban' | 'list';

/** Montant en euros arrondi, format FR. */
const fmtEuro = (n: number): string => `${Math.round(n).toLocaleString('fr-FR')} €`;

// Clés de query string considérées comme « filtres » (hors `view`). Sert à
// détecter un deep-link / drill-down : si l'URL en porte au moins une, on NE
// force PAS les défauts « Mon pipe » + « Cette année » (on respecte l'URL),
// sinon une navigation propre (sidebar) applique ces défauts. Cf #2 2026-06-27.
const FILTER_PARAM_KEYS = [
  'stage',
  'stages',
  'loss_reason',
  'contract_type_id',
  'company_id',
  'assigned_user_id',
  'apporteur_id',
  'origins',
  'min_premium',
  'max_premium',
  'min_weighted_revenue',
  'min_fees',
  'max_fees',
  'min_honoraires',
  'max_honoraires',
  'min_score',
  'stale_days',
  'created_after',
  'created_before',
  'signed_after',
  'signed_before',
  'stage_changed_after',
  'stage_changed_before',
  'effective_after',
  'effective_before',
] as const;

const LOSS_REASON_LABELS: Record<LossReason, string> = {
  price: 'Prix trop élevé',
  coverage: 'Garanties insuffisantes',
  competitor: 'Concurrent retenu',
  proximity: 'Proximité / relation',
  timing: 'Timing',
  other: 'Autre',
};

export function PipelinePage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken)!;
  const currentUserId = useAuthStore((s) => s.user?.id) ?? '';
  const role = useAuthStore((s) => s.user?.role);
  // Totaux agrégés net/rétrocom (strip liste + net par colonne Kanban) : parité
  // totale, tous rôles (décision propriétaire 2026-09-10, révise la restriction
  // direction du 2026-06-28). Le cloisonnement `scopeFilter` côté backend limite
  // déjà chaque rôle à son périmètre (un commercial ne voit que son pipe).
  const canSeeTotals = role !== undefined;
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link / drill-down ? (au montage). Si oui → on respecte l'URL telle
  // quelle. Si non (nav propre depuis la sidebar) → défaut « Mon pipe », mais
  // AUCUN filtre temporel par défaut (décision 2026-07-03 : les badges preset
  // filtrent désormais sur la date d'effet, souvent future ; un défaut « Cette
  // année » d'effet masquerait les opps neuves à effet futur). Recalculé à
  // chaque render mais seul le 1er render compte (init des useState ci-dessous).
  const isDeepLink = FILTER_PARAM_KEYS.some((k) => searchParams.has(k));

  // Init des filtres depuis l'URL : permet le drill-down depuis les charts
  // KPI Direction (`/pipeline?view=list&stages=...&loss_reason=...&...`).
  // Vue par défaut = liste (décision client 2026-04-27). Kanban accessible
  // via `?view=kanban` ou en cliquant sur l'icône dédiée.
  const [view, setView] = useState<View>(() =>
    searchParams.get('view') === 'kanban' ? 'kanban' : 'list',
  );
  const [items, setItems] = useState<OpportunityListItem[]>([]);
  // Totaux agrégés sur l'ENSEMBLE filtré (pas la page chargée) — alimente le
  // strip de la vue liste (#6) et les en-têtes Net du Kanban (#7).
  const [summary, setSummary] = useState<OpportunitiesSummary | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<string>(() => searchParams.get('stage') ?? '');
  const [minPremium, setMinPremium] = useState<string>('');
  const [maxPremium, setMaxPremium] = useState<string>('');
  // Filtres drill-down - pas de UI dédiée, juste des badges actifs au-dessus
  // de la liste et un bouton X pour les retirer un à un.
  const [stagesCsv, setStagesCsv] = useState<string>(() => searchParams.get('stages') ?? '');
  const [lossReason, setLossReason] = useState<string>(() => searchParams.get('loss_reason') ?? '');
  const [contractTypeId, setContractTypeId] = useState<string>(
    () => searchParams.get('contract_type_id') ?? '',
  );
  const [companyId, setCompanyId] = useState<string>(() => searchParams.get('company_id') ?? '');
  const [minWeightedRevenue, setMinWeightedRevenue] = useState<string>(
    () => searchParams.get('min_weighted_revenue') ?? '',
  );
  // Filtres frais & honoraires (€) - décision Q11 2026-04-30. Bornes inclusives.
  const [minFees, setMinFees] = useState<string>(() => searchParams.get('min_fees') ?? '');
  const [maxFees, setMaxFees] = useState<string>(() => searchParams.get('max_fees') ?? '');
  const [minHonoraires, setMinHonoraires] = useState<string>(
    () => searchParams.get('min_honoraires') ?? '',
  );
  const [maxHonoraires, setMaxHonoraires] = useState<string>(
    () => searchParams.get('max_honoraires') ?? '',
  );
  // Filtre « Créées » : plus de défaut à l'ouverture (cf ci-dessus). Alimenté
  // manuellement via le drawer ou par deep-link KPI (`?created_after=…`).
  const [createdAfter, setCreatedAfter] = useState<string>(
    () => searchParams.get('created_after') ?? '',
  );
  const [createdBefore, setCreatedBefore] = useState<string>(
    () => searchParams.get('created_before') ?? '',
  );
  const [signedAfter, setSignedAfter] = useState<string>(
    () => searchParams.get('signed_after') ?? '',
  );
  const [signedBefore, setSignedBefore] = useState<string>(
    () => searchParams.get('signed_before') ?? '',
  );
  const [stageChangedAfter, setStageChangedAfter] = useState<string>(
    () => searchParams.get('stage_changed_after') ?? '',
  );
  const [stageChangedBefore, setStageChangedBefore] = useState<string>(
    () => searchParams.get('stage_changed_before') ?? '',
  );
  const [effectiveAfter, setEffectiveAfter] = useState<string>(
    () => searchParams.get('effective_after') ?? '',
  );
  const [effectiveBefore, setEffectiveBefore] = useState<string>(
    () => searchParams.get('effective_before') ?? '',
  );
  // Défaut « Mon pipe » (assigned_user_id = utilisateur courant) en nav propre
  // (#2). En deep-link on respecte l'URL : si `assigned_user_id` absent → ''
  // (= Pipe équipe / tout le scope), pour ne pas casser les drill-down KPI.
  const [assignedUserId, setAssignedUserId] = useState<string>(
    () => searchParams.get('assigned_user_id') ?? (isDeepLink ? '' : currentUserId),
  );
  // Filtre apporteur(s) - multi-select. Lu depuis l'URL en CSV. Drill-down
  // depuis carte ou KPI Top apporteurs passe `?apporteur_id=<uuid>` (CSV
  // de 1 = OK), filtrage manuel depuis la modale peut empiler plusieurs.
  const [apporteurIds, setApporteurIds] = useState<string[]>(() => {
    const raw = searchParams.get('apporteur_id');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  // Filtre origines (canal d'acquisition) - multi-select. Backend accepte
  // une sentinelle 'none' pour matcher les opps sans origine (NULL côté DB).
  const [originCodes, setOriginCodes] = useState<string[]>(() => {
    const raw = searchParams.get('origins');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [minScore, setMinScore] = useState<string>(() => searchParams.get('min_score') ?? '');
  const [staleDays, setStaleDays] = useState<string>(() => searchParams.get('stale_days') ?? '');

  // --- Drawer state + listes pour les selects ---
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [companies, setCompanies] = useState<InsuranceCompany[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [apporteurs, setApporteurs] = useState<
    Array<{
      id: string;
      type: ApporteurType;
      firstName: string | null;
      lastName: string | null;
      companyName: string | null;
      company: string | null;
      isActive: boolean;
    }>
  >([]);

  // Charge les listes des dropdowns au mount - un seul fetch, persiste tant
  // que la page est montée. Les 4 endpoints sont accessibles à tous les rôles.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      configApi.listContractTypes(token),
      configApi.listInsuranceCompanies(token, true),
      usersApi.teamMembers(token),
      apporteursApi.list(token, { is_active: true }),
    ])
      .then(([cts, cos, users, apps]) => {
        if (cancelled) return;
        setContractTypes(cts);
        setCompanies(cos);
        setTeamMembers(users);
        setApporteurs(
          apps.map((a) => ({
            id: a.id,
            type: a.type,
            firstName: a.firstName,
            lastName: a.lastName,
            companyName: a.companyName,
            company: a.company,
            isActive: a.isActive,
          })),
        );
      })
      .catch(() => {
        /* silencieux - les dropdowns resteront vides, le reste fonctionne */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Ouvre la fiche opportunité (embed dans la fiche prospect). Depuis le
  // Pipeline on dispose de `prospect.id` sur chaque item, donc on navigue
  // directement vers le deep-link sans passer par le redirector.
  const openOpp = useCallback(
    (id: string) => {
      const opp = items.find((o) => o.id === id);
      if (opp) {
        navigate(`/prospects/${opp.prospect.id}?tab=opportunities&opportunity=${opp.id}`);
      } else {
        // Fallback improbable : item sorti du cache → on repasse par le redirector.
        navigate(`/opportunities/${id}`);
      }
    },
    [items, navigate],
  );

  // --- URL sync : tout filtre non vide est pushé dans l'URL en mode replace
  // pour que F5 / partage de lien preserve l'état. setSearchParams sans
  // replace ajouterait une entrée d'historique à chaque changement → spammé.
  useEffect(() => {
    const next = new URLSearchParams();
    if (view === 'list') next.set('view', 'list');
    if (stage) next.set('stage', stage);
    if (stagesCsv) next.set('stages', stagesCsv);
    if (lossReason) next.set('loss_reason', lossReason);
    if (contractTypeId) next.set('contract_type_id', contractTypeId);
    if (companyId) next.set('company_id', companyId);
    if (assignedUserId) next.set('assigned_user_id', assignedUserId);
    if (minPremium) next.set('min_premium', minPremium);
    if (maxPremium) next.set('max_premium', maxPremium);
    if (minWeightedRevenue) next.set('min_weighted_revenue', minWeightedRevenue);
    if (minFees) next.set('min_fees', minFees);
    if (maxFees) next.set('max_fees', maxFees);
    if (minHonoraires) next.set('min_honoraires', minHonoraires);
    if (maxHonoraires) next.set('max_honoraires', maxHonoraires);
    if (minScore) next.set('min_score', minScore);
    if (staleDays) next.set('stale_days', staleDays);
    if (createdAfter) next.set('created_after', createdAfter);
    if (createdBefore) next.set('created_before', createdBefore);
    if (signedAfter) next.set('signed_after', signedAfter);
    if (signedBefore) next.set('signed_before', signedBefore);
    if (stageChangedAfter) next.set('stage_changed_after', stageChangedAfter);
    if (stageChangedBefore) next.set('stage_changed_before', stageChangedBefore);
    if (effectiveAfter) next.set('effective_after', effectiveAfter);
    if (effectiveBefore) next.set('effective_before', effectiveBefore);
    if (apporteurIds.length > 0) next.set('apporteur_id', apporteurIds.join(','));
    if (originCodes.length > 0) next.set('origins', originCodes.join(','));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    stage,
    stagesCsv,
    lossReason,
    contractTypeId,
    companyId,
    assignedUserId,
    apporteurIds,
    originCodes,
    minPremium,
    maxPremium,
    minWeightedRevenue,
    minFees,
    maxFees,
    minHonoraires,
    maxHonoraires,
    minScore,
    staleDays,
    createdAfter,
    createdBefore,
    signedAfter,
    signedBefore,
    stageChangedAfter,
    stageChangedBefore,
    effectiveAfter,
    effectiveBefore,
  ]);

  function clearAllFilters(): void {
    setStage('');
    setStagesCsv('');
    setLossReason('');
    setContractTypeId('');
    setCompanyId('');
    setAssignedUserId('');
    setApporteurIds([]);
    setOriginCodes([]);
    setMinPremium('');
    setMaxPremium('');
    setMinWeightedRevenue('');
    setMinFees('');
    setMaxFees('');
    setMinHonoraires('');
    setMaxHonoraires('');
    setMinScore('');
    setStaleDays('');
    setCreatedAfter('');
    setCreatedBefore('');
    setSignedAfter('');
    setSignedBefore('');
    setStageChangedAfter('');
    setStageChangedBefore('');
    setEffectiveAfter('');
    setEffectiveBefore('');
  }

  function toggleStageInCsv(s: string): void {
    const list = stagesCsv ? stagesCsv.split(',').filter(Boolean) : [];
    const next = list.includes(s) ? list.filter((x) => x !== s) : [...list, s];
    setStagesCsv(next.join(','));
  }

  // Compteur de filtres actifs - affiché en badge sur le bouton "Filtres".
  const activeFilterCount = [
    stage,
    stagesCsv,
    lossReason,
    contractTypeId,
    companyId,
    assignedUserId,
    minPremium,
    maxPremium,
    minWeightedRevenue,
    minFees || maxFees ? '1' : '',
    minHonoraires || maxHonoraires ? '1' : '',
    minScore,
    staleDays,
    createdAfter || createdBefore ? '1' : '',
    signedAfter || signedBefore ? '1' : '',
    stageChangedAfter || stageChangedBefore ? '1' : '',
    effectiveAfter || effectiveBefore ? '1' : '',
  ].filter(Boolean).length;

  // Preset temporel actif : dérivé des bornes `effective_*` (pas un state à
  // synchroniser) — les badges filtrent désormais sur la date d'effet
  // (décision 2026-07-03). Bornes stockées en date-seule → détection
  // date-only. Si elles matchent un preset → badge surligné ; sinon → aucun.
  const activeEffectivePreset = useMemo(
    () =>
      effectiveAfter && effectiveBefore
        ? detectPeriodPresetDateOnly(effectiveAfter, effectiveBefore)
        : null,
    [effectiveAfter, effectiveBefore],
  );

  function clearFilter(key: ActiveFilterKey): void {
    // L'effet d'URL sync se chargera de propager le changement à searchParams.
    if (key === 'stage') setStage('');
    else if (key === 'stages') setStagesCsv('');
    else if (key === 'loss_reason') setLossReason('');
    else if (key === 'contract_type_id') setContractTypeId('');
    else if (key === 'company_id') setCompanyId('');
    else if (key === 'assigned_user_id') setAssignedUserId('');
    else if (key === 'apporteur_id') setApporteurIds([]);
    else if (key === 'origins') setOriginCodes([]);
    else if (key === 'premium') {
      setMinPremium('');
      setMaxPremium('');
    } else if (key === 'min_weighted_revenue') setMinWeightedRevenue('');
    else if (key === 'fees') {
      setMinFees('');
      setMaxFees('');
    } else if (key === 'honoraires') {
      setMinHonoraires('');
      setMaxHonoraires('');
    } else if (key === 'min_score') setMinScore('');
    else if (key === 'stale_days') setStaleDays('');
    else if (key === 'period_created') {
      setCreatedAfter('');
      setCreatedBefore('');
    } else if (key === 'period_signed') {
      setSignedAfter('');
      setSignedBefore('');
    } else if (key === 'period_stage_changed') {
      setStageChangedAfter('');
      setStageChangedBefore('');
    } else if (key === 'period_effective') {
      setEffectiveAfter('');
      setEffectiveBefore('');
    }
  }

  async function load(cursor?: string) {
    setLoading(true);
    setError(null);
    // Filtres partagés liste + summary : garantit que les totaux portent sur
    // le même ensemble que la liste (cursor/limit propres à la liste).
    const params = {
      stage: (stage || undefined) as OpportunityStage | undefined,
      stages: stagesCsv || undefined,
      loss_reason: (lossReason || undefined) as LossReason | undefined,
      contract_type_id: contractTypeId || undefined,
      company_id: companyId || undefined,
      min_premium: minPremium ? Number(minPremium) : undefined,
      max_premium: maxPremium ? Number(maxPremium) : undefined,
      min_weighted_revenue: minWeightedRevenue ? Number(minWeightedRevenue) : undefined,
      min_fees: minFees ? Number(minFees) : undefined,
      max_fees: maxFees ? Number(maxFees) : undefined,
      min_honoraires: minHonoraires ? Number(minHonoraires) : undefined,
      max_honoraires: maxHonoraires ? Number(maxHonoraires) : undefined,
      created_after: createdAfter || undefined,
      created_before: createdBefore || undefined,
      signed_after: signedAfter || undefined,
      signed_before: signedBefore || undefined,
      stage_changed_after: stageChangedAfter || undefined,
      stage_changed_before: stageChangedBefore || undefined,
      effective_after: effectiveAfter || undefined,
      effective_before: effectiveBefore || undefined,
      assigned_user_id: assignedUserId || undefined,
      apporteur_id: apporteurIds.length > 0 ? apporteurIds.join(',') : undefined,
      origins: originCodes.length > 0 ? originCodes.join(',') : undefined,
      min_score: minScore ? Number(minScore) : undefined,
      stale_days: staleDays ? Number(staleDays) : undefined,
    };
    try {
      // Le summary n'est rafraîchi qu'au chargement initial / changement de
      // filtre (pas en pagination « Charger plus » : les totaux sont déjà
      // globaux, inutile de refetcher).
      const [res, summaryRes] = await Promise.all([
        opportunitiesApi.list(token, { ...params, cursor, limit: 100 }),
        cursor || !canSeeTotals ? Promise.resolve(null) : opportunitiesApi.summary(token, params),
      ]);
      setItems((prev) => (cursor ? [...prev, ...res.data] : res.data));
      setNextCursor(res.nextCursor);
      if (summaryRes) setSummary(summaryRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stage,
    stagesCsv,
    lossReason,
    contractTypeId,
    companyId,
    assignedUserId,
    apporteurIds,
    originCodes,
    minPremium,
    maxPremium,
    minWeightedRevenue,
    minFees,
    maxFees,
    minHonoraires,
    maxHonoraires,
    minScore,
    staleDays,
    createdAfter,
    createdBefore,
    signedAfter,
    signedBefore,
    stageChangedAfter,
    stageChangedBefore,
    effectiveAfter,
    effectiveBefore,
  ]);

  // Realtime : toute mutation d'une opportunité (par soi ou un collègue)
  // rafraîchit la liste complète depuis la première page. Pas de filtre par
  // id : même si l'opp mutée n'est pas dans la page visible, la création
  // d'une nouvelle doit apparaître, et un changeStage déplace l'opp entre
  // colonnes - plus simple et sûr de refetch que de patcher en local.
  useRealtimeEvent(
    REALTIME_EVENTS.OPPORTUNITY_UPDATED,
    useCallback(() => {
      void load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  async function moveOpp(oppId: string, targetStage: OpportunityStage) {
    // Garde UX : court-circuite le round-trip quand la règle métier
    // « Devis émis requiert un devis PDF » n'est pas satisfaite. Le backend
    // enforce la même règle (source de vérité) mais afficher un message
    // explicite localement évite un aller-retour et un 422 technique.
    if (targetStage === 'quote_sent') {
      const target = items.find((o) => o.id === oppId);
      if (target && target._count.quotes === 0) {
        toast({
          tone: 'warning',
          title: 'Devis manquant',
          message:
            'Chargez au moins un devis PDF dans l\u2019opportunité avant de la passer à « Devis émis ».',
        });
        return;
      }
    }

    try {
      const updated = await opportunitiesApi.changeStage(token, oppId, targetStage);
      // L'endpoint PATCH /stage renvoie le détail brut Prisma (DETAIL_INCLUDES)
      // qui n'inclut PAS `checklistProgress` ni `_count` - calculés uniquement
      // par l'endpoint list. On merge dans l'item existant pour préserver ces
      // champs UI-only.
      setItems((prev) =>
        prev.map((o) =>
          o.id === oppId ? { ...o, ...(updated as Partial<OpportunityListItem>) } : o,
        ),
      );
      toast({
        tone: 'success',
        message: `${updated.reference} → ${STAGE_LABELS[targetStage]}`,
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Transition impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title="Pipeline"
        className="mb-2"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-white px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:border-accent/40 hover:text-brand"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filtres
              {activeFilterCount > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <div className="inline-flex overflow-hidden rounded-md border border-border-subtle bg-white">
              <button
                type="button"
                onClick={() => setView('kanban')}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                  view === 'kanban'
                    ? 'bg-accent/10 text-accent'
                    : 'text-text-muted hover:text-brand',
                )}
                aria-pressed={view === 'kanban'}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Kanban
              </button>
              <button
                type="button"
                onClick={() => setView('list')}
                className={cn(
                  'flex items-center gap-1.5 border-l border-border-subtle px-3 py-1.5 text-xs font-medium transition-colors',
                  view === 'list' ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-brand',
                )}
                aria-pressed={view === 'list'}
              >
                <ListIcon className="h-3.5 w-3.5" />
                Liste
              </button>
            </div>
          </div>
        }
      />

      {/* Sous-titre + totaux (liste ET kanban) sur une ligne pleine largeur : le
          sous-titre à gauche, les totaux alignés à droite — au même bord que
          les boutons « Liste » et « 12 derniers mois ». #6 2026-06-27, étendu au
          kanban 2026-07-02 (mêmes droits, même source `summary` = requête DB
          agrégée sur tout l'ensemble filtré, indépendante des cartes chargées).
          Le Net par colonne du kanban est conservé en complément. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <p className="text-sm text-text-muted">Vue d&apos;ensemble des opportunités commerciales</p>
        {canSeeTotals && summary && <PipelineTotalsInline summary={summary} />}
      </div>

      {/* Bascule de périmètre (Mon pipe / Pipe de l'équipe) + presets temporels
          sur la DATE D'EFFET (décision 2026-07-03). Même design que le
          dashboard. Défaut au chargement (nav propre) : Mon pipe, sans filtre
          temporel. Le filtre « Créées » reste dispo manuellement dans le drawer. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-border-subtle bg-white">
          <button
            type="button"
            onClick={() => setAssignedUserId(currentUserId)}
            aria-pressed={currentUserId !== '' && assignedUserId === currentUserId}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
              currentUserId !== '' && assignedUserId === currentUserId
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-brand',
            )}
          >
            <User className="h-3.5 w-3.5" />
            Mon pipe
          </button>
          <button
            type="button"
            onClick={() => setAssignedUserId('')}
            aria-pressed={assignedUserId === ''}
            className={cn(
              'flex items-center gap-1.5 border-l border-border-subtle px-3 py-1.5 text-xs font-medium transition-colors',
              assignedUserId === ''
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-brand',
            )}
          >
            <Users className="h-3.5 w-3.5" />
            Pipe de l&apos;équipe
          </button>
        </div>
        <DateRangeBadges
          activePresetKey={activeEffectivePreset}
          presets={PERIOD_PRESETS}
          // Les badges filtrent sur la date d'effet. Le filtre `effective_*` est
          // validé `YYYY-MM-DD` côté backend (regex stricte) alors que les
          // presets émettent un ISO datetime complet → on tronque à la date
          // (slice 0,10), sinon 422 (gotcha connu).
          onChange={(r) => {
            setEffectiveAfter(r.from.slice(0, 10));
            setEffectiveBefore(r.to.slice(0, 10));
          }}
          align="end"
        />
      </div>

      <ActiveFilters
        stage={stage}
        stagesCsv={stagesCsv}
        lossReason={lossReason}
        contractTypeId={contractTypeId}
        contractTypes={contractTypes}
        companyId={companyId}
        companies={companies}
        assignedUserId={assignedUserId}
        teamMembers={teamMembers}
        apporteurIds={apporteurIds}
        apporteurs={apporteurs}
        originCodes={originCodes}
        minPremium={minPremium}
        maxPremium={maxPremium}
        minWeightedRevenue={minWeightedRevenue}
        minFees={minFees}
        maxFees={maxFees}
        minHonoraires={minHonoraires}
        maxHonoraires={maxHonoraires}
        minScore={minScore}
        staleDays={staleDays}
        createdAfter={createdAfter}
        createdBefore={createdBefore}
        signedAfter={signedAfter}
        signedBefore={signedBefore}
        stageChangedAfter={stageChangedAfter}
        stageChangedBefore={stageChangedBefore}
        effectiveAfter={effectiveAfter}
        effectiveBefore={effectiveBefore}
        onClear={clearFilter}
      />

      {error && (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      )}

      {view === 'kanban' ? (
        <KanbanView
          items={items}
          summary={summary}
          canSeeTotals={canSeeTotals}
          loading={loading && items.length === 0}
          onMove={moveOpp}
          onOpen={openOpp}
        />
      ) : (
        <ListView items={items} loading={loading && items.length === 0} onOpen={openOpp} />
      )}

      {nextCursor && view === 'list' && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => void load(nextCursor)} disabled={loading}>
            {loading ? 'Chargement…' : 'Charger plus'}
          </Button>
        </div>
      )}

      <FiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeCount={activeFilterCount}
        onClearAll={clearAllFilters}
        // valeurs
        stage={stage}
        stagesCsv={stagesCsv}
        lossReason={lossReason}
        contractTypeId={contractTypeId}
        companyId={companyId}
        assignedUserId={assignedUserId}
        apporteurIds={apporteurIds}
        originCodes={originCodes}
        minPremium={minPremium}
        maxPremium={maxPremium}
        minWeightedRevenue={minWeightedRevenue}
        minFees={minFees}
        maxFees={maxFees}
        minHonoraires={minHonoraires}
        maxHonoraires={maxHonoraires}
        minScore={minScore}
        staleDays={staleDays}
        createdAfter={createdAfter}
        createdBefore={createdBefore}
        signedAfter={signedAfter}
        signedBefore={signedBefore}
        stageChangedAfter={stageChangedAfter}
        stageChangedBefore={stageChangedBefore}
        effectiveAfter={effectiveAfter}
        effectiveBefore={effectiveBefore}
        // listes pour les selects
        contractTypes={contractTypes}
        companies={companies}
        teamMembers={teamMembers}
        apporteurs={apporteurs}
        // setters
        onStage={setStage}
        onApporteurIds={setApporteurIds}
        onOriginCodes={setOriginCodes}
        onToggleStage={toggleStageInCsv}
        onLossReason={setLossReason}
        onContractTypeId={setContractTypeId}
        onCompanyId={setCompanyId}
        onAssignedUserId={setAssignedUserId}
        onMinPremium={setMinPremium}
        onMaxPremium={setMaxPremium}
        onMinWeightedRevenue={setMinWeightedRevenue}
        onMinFees={setMinFees}
        onMaxFees={setMaxFees}
        onMinHonoraires={setMinHonoraires}
        onMaxHonoraires={setMaxHonoraires}
        onMinScore={setMinScore}
        onStaleDays={setStaleDays}
        onCreatedAfter={setCreatedAfter}
        onCreatedBefore={setCreatedBefore}
        onSignedAfter={setSignedAfter}
        onSignedBefore={setSignedBefore}
        onStageChangedAfter={setStageChangedAfter}
        onStageChangedBefore={setStageChangedBefore}
        onEffectiveAfter={setEffectiveAfter}
        onEffectiveBefore={setEffectiveBefore}
      />
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Active filters - pills affichées au-dessus de la liste/kanban. Source de
// vérité pour visualiser ce qui est filtré + pouvoir retirer chaque filtre
// individuellement sans rouvrir le drawer.
// ---------------------------------------------------------------------------

type ActiveFilterKey =
  | 'stage'
  | 'stages'
  | 'loss_reason'
  | 'contract_type_id'
  | 'company_id'
  | 'assigned_user_id'
  | 'apporteur_id'
  | 'origins'
  | 'premium'
  | 'min_weighted_revenue'
  | 'fees'
  | 'honoraires'
  | 'min_score'
  | 'stale_days'
  | 'period_created'
  | 'period_signed'
  | 'period_stage_changed'
  | 'period_effective';

function ActiveFilters(props: {
  stage: string;
  stagesCsv: string;
  lossReason: string;
  contractTypeId: string;
  contractTypes: ContractType[];
  companyId: string;
  companies: InsuranceCompany[];
  assignedUserId: string;
  teamMembers: TeamMember[];
  apporteurIds: string[];
  apporteurs: Array<{
    id: string;
    type: ApporteurType;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    company: string | null;
  }>;
  originCodes: string[];
  minPremium: string;
  maxPremium: string;
  minWeightedRevenue: string;
  minFees: string;
  maxFees: string;
  minHonoraires: string;
  maxHonoraires: string;
  minScore: string;
  staleDays: string;
  createdAfter: string;
  createdBefore: string;
  signedAfter: string;
  signedBefore: string;
  stageChangedAfter: string;
  stageChangedBefore: string;
  effectiveAfter: string;
  effectiveBefore: string;
  onClear: (key: ActiveFilterKey) => void;
}) {
  const filters: Array<{ key: ActiveFilterKey; label: string }> = [];
  if (props.stage) {
    filters.push({
      key: 'stage',
      label: `Étape : ${(STAGE_LABELS as Record<string, string>)[props.stage] ?? props.stage}`,
    });
  }
  if (props.stagesCsv) {
    const labels = props.stagesCsv
      .split(',')
      .map((s) => (STAGE_LABELS as Record<string, string>)[s.trim()] ?? s.trim())
      .join(' · ');
    filters.push({ key: 'stages', label: `Étapes : ${labels}` });
  }
  if (props.lossReason) {
    filters.push({
      key: 'loss_reason',
      label: `Motif de perte : ${LOSS_REASON_LABELS[props.lossReason as LossReason] ?? props.lossReason}`,
    });
  }
  if (props.contractTypeId) {
    const ct = props.contractTypes.find((t) => t.id === props.contractTypeId);
    filters.push({
      key: 'contract_type_id',
      label: `Type : ${ct ? ct.code : 'filtré'}`,
    });
  }
  if (props.companyId) {
    const co = props.companies.find((c) => c.id === props.companyId);
    filters.push({
      key: 'company_id',
      label: `Compagnie : ${co ? co.name : 'filtrée'}`,
    });
  }
  if (props.assignedUserId) {
    const u = props.teamMembers.find((m) => m.id === props.assignedUserId);
    filters.push({
      key: 'assigned_user_id',
      label: `Commercial : ${u ? `${u.firstName} ${u.lastName}` : 'filtré'}`,
    });
  }
  if (props.apporteurIds.length > 0) {
    const labels = props.apporteurIds
      .map((id) => {
        const a = props.apporteurs.find((x) => x.id === id);
        return a ? apporteurDisplayName(a) : 'filtré';
      })
      .join(' · ');
    filters.push({ key: 'apporteur_id', label: `Apporteur : ${labels}` });
  }
  if (props.originCodes.length > 0) {
    const labels = props.originCodes
      .map((c) =>
        c === 'none' ? 'Non renseignée' : (ORIGIN_VISUAL[c as OpportunityOrigin]?.label ?? c),
      )
      .join(' · ');
    filters.push({ key: 'origins', label: `Origine : ${labels}` });
  }
  if (props.minPremium || props.maxPremium) {
    const min = props.minPremium ? `${props.minPremium} €` : '−∞';
    const max = props.maxPremium ? `${props.maxPremium} €` : '+∞';
    filters.push({ key: 'premium', label: `Prime HT : ${min} → ${max}` });
  }
  if (props.minFees || props.maxFees) {
    const min = props.minFees ? `${props.minFees} €` : '−∞';
    const max = props.maxFees ? `${props.maxFees} €` : '+∞';
    filters.push({ key: 'fees', label: `Frais : ${min} → ${max}` });
  }
  if (props.minHonoraires || props.maxHonoraires) {
    const min = props.minHonoraires ? `${props.minHonoraires} €` : '−∞';
    const max = props.maxHonoraires ? `${props.maxHonoraires} €` : '+∞';
    filters.push({ key: 'honoraires', label: `Honoraires : ${min} → ${max}` });
  }
  if (props.minWeightedRevenue) {
    filters.push({
      key: 'min_weighted_revenue',
      label: `CA pondéré ≥ ${props.minWeightedRevenue} €`,
    });
  }
  if (props.minScore) {
    filters.push({ key: 'min_score', label: `Score ≥ ${props.minScore}` });
  }
  if (props.staleDays) {
    filters.push({
      key: 'stale_days',
      label: `Sans activité ≥ ${props.staleDays} j`,
    });
  }
  if (props.createdAfter || props.createdBefore) {
    filters.push({
      key: 'period_created',
      label: `Créées : ${formatPeriod(props.createdAfter, props.createdBefore)}`,
    });
  }
  if (props.signedAfter || props.signedBefore) {
    filters.push({
      key: 'period_signed',
      label: `Signées : ${formatPeriod(props.signedAfter, props.signedBefore)}`,
    });
  }
  if (props.stageChangedAfter || props.stageChangedBefore) {
    filters.push({
      key: 'period_stage_changed',
      label: `Étape changée : ${formatPeriod(props.stageChangedAfter, props.stageChangedBefore)}`,
    });
  }
  if (props.effectiveAfter || props.effectiveBefore) {
    filters.push({
      key: 'period_effective',
      label: `Date d'effet : ${formatPeriod(props.effectiveAfter, props.effectiveBefore)}`,
    });
  }
  if (filters.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="text-[11px] uppercase tracking-wider text-text-muted">Filtres actifs</span>
      {filters.map((f) => (
        <span
          key={f.key}
          className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
        >
          {f.label}
          <button
            type="button"
            onClick={() => props.onClear(f.key)}
            aria-label={`Retirer le filtre ${f.label}`}
            className="rounded-full p-0.5 hover:bg-accent/20"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

function formatPeriod(startIso: string, endIso: string): string {
  const fmt = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '?';
  if (startIso && endIso) return `${fmt(startIso)} → ${fmt(endIso)}`;
  if (startIso) return `depuis ${fmt(startIso)}`;
  return `jusqu'à ${fmt(endIso)}`;
}

// ---------------------------------------------------------------------------
// Drawer "Filtres" - toutes les options de filtrage groupées par catégorie.
// Mises à jour en live (pas de bouton "Appliquer") : chaque changement
// déclenche immédiatement le re-fetch via les useEffect du parent.
// ---------------------------------------------------------------------------

interface FiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  activeCount: number;
  onClearAll: () => void;
  // valeurs
  stage: string;
  stagesCsv: string;
  lossReason: string;
  contractTypeId: string;
  companyId: string;
  assignedUserId: string;
  apporteurIds: string[];
  originCodes: string[];
  minPremium: string;
  maxPremium: string;
  minWeightedRevenue: string;
  minFees: string;
  maxFees: string;
  minHonoraires: string;
  maxHonoraires: string;
  minScore: string;
  staleDays: string;
  createdAfter: string;
  createdBefore: string;
  signedAfter: string;
  signedBefore: string;
  stageChangedAfter: string;
  stageChangedBefore: string;
  effectiveAfter: string;
  effectiveBefore: string;
  // listes pour les selects
  contractTypes: ContractType[];
  companies: InsuranceCompany[];
  teamMembers: TeamMember[];
  apporteurs: Array<{
    id: string;
    type: ApporteurType;
    firstName: string | null;
    lastName: string | null;
    companyName: string | null;
    company: string | null;
    isActive: boolean;
  }>;
  // setters
  onStage: (v: string) => void;
  onToggleStage: (s: string) => void;
  onLossReason: (v: string) => void;
  onContractTypeId: (v: string) => void;
  onCompanyId: (v: string) => void;
  onAssignedUserId: (v: string) => void;
  onApporteurIds: (ids: string[]) => void;
  onOriginCodes: (codes: string[]) => void;
  onMinPremium: (v: string) => void;
  onMaxPremium: (v: string) => void;
  onMinWeightedRevenue: (v: string) => void;
  onMinFees: (v: string) => void;
  onMaxFees: (v: string) => void;
  onMinHonoraires: (v: string) => void;
  onMaxHonoraires: (v: string) => void;
  onMinScore: (v: string) => void;
  onStaleDays: (v: string) => void;
  onCreatedAfter: (v: string) => void;
  onCreatedBefore: (v: string) => void;
  onSignedAfter: (v: string) => void;
  onSignedBefore: (v: string) => void;
  onStageChangedAfter: (v: string) => void;
  onStageChangedBefore: (v: string) => void;
  onEffectiveAfter: (v: string) => void;
  onEffectiveBefore: (v: string) => void;
}

function FiltersDrawer(p: FiltersDrawerProps) {
  // Convertit '2026-04-01T00:00:00.000Z' ↔ '2026-04-01' pour <input type="date">.
  const isoToDate = (iso: string) => (iso ? iso.slice(0, 10) : '');
  const dateToIso = (date: string) => (date ? new Date(`${date}T00:00:00Z`).toISOString() : '');

  const selectedStages = p.stagesCsv ? p.stagesCsv.split(',').filter(Boolean) : [];

  return (
    <Drawer
      open={p.open}
      onClose={p.onClose}
      title="Filtres"
      width="md"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-text-muted">
            {p.activeCount === 0
              ? 'Aucun filtre actif'
              : `${p.activeCount} filtre${p.activeCount > 1 ? 's' : ''} actif${p.activeCount > 1 ? 's' : ''}`}
          </span>
          <Button variant="secondary" onClick={p.onClearAll} disabled={p.activeCount === 0}>
            Tout effacer
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* ===== Étapes ===== */}
        <FilterSection title="Étape">
          <Select value={p.stage} onChange={(e) => p.onStage(e.target.value)}>
            <option value="">Toutes étapes</option>
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABELS[s]}
              </option>
            ))}
            <option value="lost">{STAGE_LABELS.lost}</option>
            {/* `no_follow_up` est masqué côté UI V1 (refonte 2026-05-21, décision Q7).
                La valeur reste dans l'enum pour rétro-compatibilité (opps importées
                avec ce stage restent visibles avec leur badge "Sans suite"). */}
          </Select>
          <div className="mt-3">
            <FilterLabel>Multi-étapes (combine OR)</FilterLabel>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {([...PIPELINE_STAGES, 'lost'] as readonly string[]).map((s) => {
                const active = selectedStages.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => p.onToggleStage(s)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                      active
                        ? 'border-accent bg-accent text-white'
                        : 'border-border-subtle bg-white text-text-muted hover:border-accent/40 hover:text-brand',
                    )}
                  >
                    {(STAGE_LABELS as Record<string, string>)[s] ?? s}
                  </button>
                );
              })}
            </div>
          </div>
        </FilterSection>

        {/* ===== Catégorisation ===== */}
        <FilterSection title="Catégorisation">
          <FilterLabel>Type de contrat</FilterLabel>
          <Select value={p.contractTypeId} onChange={(e) => p.onContractTypeId(e.target.value)}>
            <option value="">Tous types</option>
            {p.contractTypes
              .filter((t) => t.isActive)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} - {t.label}
                </option>
              ))}
          </Select>

          {/* Origines (canal d'acquisition) — multi-select. La sentinelle
              'none' permet de filtrer les opps SANS origine (NULL côté DB,
              utile pour identifier les opps historiques à compléter). */}
          <FilterLabel className="mt-3">Origine</FilterLabel>
          <MultiSelectDropdown
            options={[
              ...ORIGIN_ORDER.map((code) => ({
                value: code,
                label: ORIGIN_VISUAL[code].label,
              })),
              { value: 'none', label: 'Non renseignée', sublabel: 'sans origine' },
            ]}
            selected={p.originCodes}
            onChange={p.onOriginCodes}
            placeholder="Toutes origines"
            headerLabel="Origines"
            pluralLabel="origines"
          />

          <FilterLabel className="mt-3">Compagnie d&apos;assurance</FilterLabel>
          <Select value={p.companyId} onChange={(e) => p.onCompanyId(e.target.value)}>
            <option value="">Toutes compagnies</option>
            {p.companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <FilterLabel className="mt-3">Motif de perte</FilterLabel>
          <Select value={p.lossReason} onChange={(e) => p.onLossReason(e.target.value)}>
            <option value="">Tous motifs</option>
            {(Object.keys(LOSS_REASON_LABELS) as LossReason[]).map((r) => (
              <option key={r} value={r}>
                {LOSS_REASON_LABELS[r]}
              </option>
            ))}
          </Select>
        </FilterSection>

        {/* ===== Affectation ===== */}
        <FilterSection title="Affectation">
          <FilterLabel>Commercial</FilterLabel>
          <Select value={p.assignedUserId} onChange={(e) => p.onAssignedUserId(e.target.value)}>
            <option value="">Tous commerciaux</option>
            {p.teamMembers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </Select>

          <FilterLabel className="mt-3">Apporteur</FilterLabel>
          <MultiSelectDropdown
            options={p.apporteurs.map((a) => ({
              value: a.id,
              label: `${apporteurDisplayName(a)}${a.isActive ? '' : ' — désactivé'}`,
              // person : sublabel = employeur (a.company)
              // company : sublabel = badge type (« Entreprise »)
              sublabel: a.type === 'company' ? 'Entreprise' : (a.company ?? undefined),
            }))}
            selected={p.apporteurIds}
            onChange={p.onApporteurIds}
            placeholder="Tous apporteurs"
            headerLabel="Apporteurs"
            pluralLabel="apporteurs"
          />
        </FilterSection>

        {/* ===== Financier ===== */}
        <FilterSection title="Financier">
          <FilterLabel>Prime HT (€)</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={p.minPremium}
              onChange={(e) => p.onMinPremium(e.target.value)}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="number"
              placeholder="Max"
              value={p.maxPremium}
              onChange={(e) => p.onMaxPremium(e.target.value)}
            />
          </div>

          <FilterLabel className="mt-3">Frais (€)</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={p.minFees}
              onChange={(e) => p.onMinFees(e.target.value)}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="number"
              placeholder="Max"
              value={p.maxFees}
              onChange={(e) => p.onMaxFees(e.target.value)}
            />
          </div>

          <FilterLabel className="mt-3">Honoraires (€)</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={p.minHonoraires}
              onChange={(e) => p.onMinHonoraires(e.target.value)}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="number"
              placeholder="Max"
              value={p.maxHonoraires}
              onChange={(e) => p.onMaxHonoraires(e.target.value)}
            />
          </div>

          <FilterLabel className="mt-3">CA pondéré minimum (€)</FilterLabel>
          <Input
            type="number"
            placeholder="ex. 1000"
            value={p.minWeightedRevenue}
            onChange={(e) => p.onMinWeightedRevenue(e.target.value)}
          />

          <FilterLabel className="mt-3">Score minimum (0–100)</FilterLabel>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="ex. 50"
            value={p.minScore}
            onChange={(e) => p.onMinScore(e.target.value)}
          />
        </FilterSection>

        {/* ===== Activité ===== */}
        <FilterSection title="Activité">
          <FilterLabel>Sans activité depuis (jours)</FilterLabel>
          <Input
            type="number"
            min={1}
            placeholder="ex. 30"
            value={p.staleDays}
            onChange={(e) => p.onStaleDays(e.target.value)}
          />
        </FilterSection>

        {/* ===== Périodes ===== */}
        <FilterSection title="Périodes">
          <FilterLabel>Date de création</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="date"
              value={isoToDate(p.createdAfter)}
              onChange={(e) => p.onCreatedAfter(dateToIso(e.target.value))}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="date"
              value={isoToDate(p.createdBefore)}
              onChange={(e) => p.onCreatedBefore(dateToIso(e.target.value))}
            />
          </div>

          <FilterLabel className="mt-3">Date de signature</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="date"
              value={isoToDate(p.signedAfter)}
              onChange={(e) => p.onSignedAfter(dateToIso(e.target.value))}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="date"
              value={isoToDate(p.signedBefore)}
              onChange={(e) => p.onSignedBefore(dateToIso(e.target.value))}
            />
          </div>

          <FilterLabel className="mt-3">Date de changement d&apos;étape</FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="date"
              value={isoToDate(p.stageChangedAfter)}
              onChange={(e) => p.onStageChangedAfter(dateToIso(e.target.value))}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="date"
              value={isoToDate(p.stageChangedBefore)}
              onChange={(e) => p.onStageChangedBefore(dateToIso(e.target.value))}
            />
          </div>

          <FilterLabel className="mt-3">
            Date d&apos;effet (prise d&apos;effet du contrat)
          </FilterLabel>
          <div className="mt-1.5 flex items-center gap-2">
            <Input
              type="date"
              value={p.effectiveAfter}
              onChange={(e) => p.onEffectiveAfter(e.target.value)}
            />
            <span className="text-text-muted">→</span>
            <Input
              type="date"
              value={p.effectiveBefore}
              onChange={(e) => p.onEffectiveBefore(e.target.value)}
            />
          </div>
        </FilterSection>
      </div>
    </Drawer>
  );
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FilterLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <label className={cn('block text-xs font-medium text-text-primary', className)}>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Kanban
// ---------------------------------------------------------------------------

// Totaux compacts (vue liste), rendus INLINE sur la ligne du sous-titre
// (alignement géré par le parent). Sur l'ENSEMBLE des opps filtrées (pas la
// page chargée) — #6 2026-06-27.
function PipelineTotalsInline({ summary }: { summary: OpportunitiesSummary }) {
  const { commission, feesHonoraires, retrocom, net } = summary.overall;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <TotalItem label="Com." value={fmtEuro(commission)} />
      <span className="text-border-subtle">·</span>
      <TotalItem label="Frais + hono." value={fmtEuro(feesHonoraires)} />
      <span className="text-border-subtle">·</span>
      <TotalItem label="Rétrocom" value={fmtEuro(retrocom)} />
      <span className="text-border-subtle">·</span>
      <TotalItem label="Net" value={fmtEuro(net)} emphasis />
    </span>
  );
}

function TotalItem({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-[11px] uppercase tracking-wider text-text-muted">{label}</span>
      <span className={cn('text-sm font-semibold', emphasis ? 'text-accent' : 'text-brand')}>
        {value}
      </span>
    </span>
  );
}

function KanbanView({
  items,
  summary,
  canSeeTotals,
  loading,
  onMove,
  onOpen,
}: {
  items: OpportunityListItem[];
  summary: OpportunitiesSummary | null;
  canSeeTotals: boolean;
  loading: boolean;
  onMove: (oppId: string, stage: OpportunityStage) => void;
  onOpen: (oppId: string) => void;
}) {
  const grouped = useMemo(() => {
    const m: Record<OpportunityStage, OpportunityListItem[]> = {
      suspect: [],
      contacted: [],
      meeting_booked: [],
      meeting_done: [],
      awaiting_client_docs: [],
      awaiting_quote_supplier: [],
      quote_sent: [],
      won: [],
      lost: [],
      no_follow_up: [],
    };
    for (const o of items) {
      m[o.stage].push(o);
    }
    return m;
  }, [items]);

  const [dragging, setDragging] = useState<string | null>(null);

  // --- Drag-to-scroll horizontal sur les headers ---
  // Pattern : on enregistre la position X au mousedown sur un header, puis on
  // translate `scrollLeft` au mousemove. On garde la souris partout (pas
  // seulement sur le header) pour permettre un swipe ample. On désactive le
  // drag HTML5 natif des cartes pendant le scroll-drag pour éviter les
  // conflits (HTML5 dragstart vs mousemove).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ active: boolean; startX: number; scrollLeft: number }>({
    active: false,
    startX: 0,
    scrollLeft: 0,
  });
  const [scrolling, setScrolling] = useState(false);

  function onHeaderPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (!scrollRef.current) return;
    // N'intercepte pas le clic gauche sur un autre élément interactif -
    // headers uniquement (déclenché depuis le wrapper du header).
    dragRef.current.active = true;
    dragRef.current.startX = e.pageX;
    dragRef.current.scrollLeft = scrollRef.current.scrollLeft;
    setScrolling(true);
    // Capture le pointeur pour continuer à recevoir les events même hors zone.
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onHeaderPointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (!dragRef.current.active || !scrollRef.current) return;
    const dx = e.pageX - dragRef.current.startX;
    scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx;
  }

  function onHeaderPointerUp(e: React.PointerEvent<HTMLDivElement>): void {
    dragRef.current.active = false;
    setScrolling(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  if (loading) {
    return (
      <div className="flex gap-5 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map((s) => (
          <div key={s} className="min-w-[280px]">
            <Skeleton className="mb-3 h-7 w-40" />
            <Skeleton className="mb-2 h-16 w-full" />
            <Skeleton className="mb-2 h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex gap-5 overflow-x-auto pb-4">
      {PIPELINE_STAGES.map((s) => {
        const list = grouped[s];
        // Total de colonne = NET sur l'ENSEMBLE des opps de l'étape (com +
        // frais + honoraires − rétrocom), pas seulement les cartes chargées
        // (décision 2026-06-27, #7). Vient du summary backend agrégé.
        const bucket = summary?.byStage[s];
        return (
          <div
            key={s}
            className={cn(
              'flex min-w-[300px] max-w-[300px] flex-col overflow-hidden rounded-lg border border-border-subtle bg-gray-50',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData('text/plain');
              if (id) {
                onMove(id, s);
                setDragging(null);
              }
            }}
          >
            {/* Barre colorée d'identification de l'étape */}
            <div className={cn('h-[3px] w-full', STAGE_VISUAL[s].bar)} />
            {/* Header : titre semibold, count en pill, total en ligne dessous.
                Cliquer-glisser sur le header → scroll horizontal du board. */}
            <div
              onPointerDown={onHeaderPointerDown}
              onPointerMove={onHeaderPointerMove}
              onPointerUp={onHeaderPointerUp}
              onPointerCancel={onHeaderPointerUp}
              className={cn(
                'select-none px-3 pb-2 pt-3',
                scrolling ? 'cursor-grabbing' : 'cursor-grab',
              )}
              title="Cliquer-glisser pour faire défiler"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold tracking-tight text-brand">
                  {STAGE_LABELS[s]}
                </h3>
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-semibold text-text-muted ring-1 ring-border-subtle">
                  {list.length}
                </span>
              </div>
              {canSeeTotals && (
                <div className="mt-0.5 text-[11px] text-text-muted">
                  {!summary ? (
                    '…'
                  ) : bucket && bucket.count > 0 ? (
                    <Tooltip
                      side="bottom"
                      align="start"
                      content={
                        <span className="flex flex-col gap-0.5">
                          <span>Commission {fmtEuro(bucket.commission)}</span>
                          <span>Frais + honoraires {fmtEuro(bucket.feesHonoraires)}</span>
                          <span>− Rétrocom {fmtEuro(bucket.retrocom)}</span>
                          <span className="mt-0.5 border-t border-white/20 pt-0.5 font-semibold">
                            = Net {fmtEuro(bucket.net)}
                          </span>
                        </span>
                      }
                    >
                      <span className="cursor-help border-b border-dotted border-text-muted/40">
                        {`${fmtEuro(bucket.net)} net`}
                      </span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 px-3 pb-3">
              {list.map((opp) => (
                <KanbanCard
                  key={opp.id}
                  opp={opp}
                  onOpen={() => onOpen(opp.id)}
                  dragging={dragging === opp.id}
                  onDragStart={() => setDragging(opp.id)}
                  onDragEnd={() => setDragging(null)}
                />
              ))}
              {list.length === 0 && (
                <div className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-text-muted">
                  Vide
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Barres de couleur en haut de chaque colonne - alignées sur les teintes du
// `PipelineStageBadge` pour cohérence visuelle. Permet de scanner rapidement
// les étapes sans lire le titre.
// La palette des barres de colonne est centralisée dans `STAGE_VISUAL`
// (source unique, cf services/opportunities.api.ts). Auparavant cette page
// dupliquait son propre `STAGE_BAR_COLOR` — supprimé 2026-05-21 (rétrocheck
// refonte pipeline) pour éviter le drift à chaque retouche de teinte.

/** Extrait la dernière portion d'une référence après le dernier `-`. */
function lastRefSegment(ref: string): string {
  const i = ref.lastIndexOf('-');
  return i >= 0 ? ref.slice(i + 1) : ref;
}

function KanbanCard({
  opp,
  onOpen,
  dragging,
  onDragStart,
  onDragEnd,
}: {
  opp: OpportunityListItem;
  onOpen: () => void;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const score = opp.effectiveScore ? Number(opp.effectiveScore) : null;
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', opp.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={cn(
        'group rounded-md border border-border-subtle bg-white p-3 text-left shadow-sm transition-all hover:border-accent/40 hover:shadow-popover',
        dragging && 'opacity-40',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-brand group-hover:text-accent">
            {displayName(opp.prospect)}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-text-muted">
            <span className="font-mono">#{lastRefSegment(opp.reference)}</span> ·{' '}
            {opp.contractType.code}
            {/* Refonte 2026-05-23 : origin lu depuis le prospect parent
                (le champ a été déplacé depuis Opportunity). */}
            {opp.prospect.origin && (
              <>
                {' · '}
                <span
                  className={cn(
                    'inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium',
                    ORIGIN_VISUAL[opp.prospect.origin].badge,
                  )}
                  title={`Origine : ${ORIGIN_VISUAL[opp.prospect.origin].label}`}
                >
                  {ORIGIN_VISUAL[opp.prospect.origin].label}
                </span>
              </>
            )}
          </div>
        </div>
        {score !== null && <ScoreBadge score={score} divergent={false} />}
      </div>
      {/* Date du dernier RDV (vert/rouge), juste au-dessus de la ligne
          Commercial — uniquement pour les opps RDV pris / RDV effectué. */}
      {opp.lastMeeting && (
        <div className="mt-2 text-[11px]">
          <RdvDateInline meeting={opp.lastMeeting} />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-text-muted">
        <span className="truncate">
          {opp.assignedUser.firstName[0]}. {opp.assignedUser.lastName}
        </span>
        {/* Montant affiché = commission (décision 2026-06-18, remplace la
            Prime HT). « - » si commission nulle / non calculée. */}
        <span className="font-medium text-brand" title="Commission">
          {opp.commissionAmount && Number(opp.commissionAmount) > 0
            ? `${Number(opp.commissionAmount).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
            : '-'}
        </span>
      </div>
      {opp.checklistProgress.required > 0 && <ChecklistBadge progress={opp.checklistProgress} />}
    </button>
  );
}

function ChecklistBadge({
  progress,
}: {
  progress: { required: number; fulfilled: number; percent: number };
}) {
  const complete = progress.percent === 100;
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            complete ? 'bg-success-600' : 'bg-accent',
          )}
          style={{ width: `${progress.percent}%` }}
        />
      </div>
      <span className="text-[10px] font-medium text-text-muted">
        {progress.fulfilled}/{progress.required}
      </span>
    </div>
  );
}

/**
 * Date du dernier RDV (event meeting) rattaché à l'opp, colorée selon que le
 * RDV est marqué fait (vert) ou non (rouge). N'est rendue que pour les opps
 * en meeting_booked / meeting_done (le backend ne renvoie `lastMeeting` que
 * pour ces stades). Décision produit 2026-06-05.
 */
function RdvDateInline({ meeting }: { meeting: { scheduledAt: string; done: boolean } }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap font-medium',
        meeting.done ? 'text-success-600' : 'text-danger-600',
      )}
      title={meeting.done ? 'RDV effectué' : 'RDV non effectué'}
    >
      <CalendarCheck className="h-3 w-3 shrink-0" />
      {new Date(meeting.scheduledAt).toLocaleDateString('fr-FR')}
    </span>
  );
}

function ScoreBadge({ score, divergent }: { score: number; divergent: boolean }) {
  const color =
    score >= 70
      ? 'bg-success-50 text-success-600'
      : score >= 40
        ? 'bg-warning-50 text-warning-600'
        : 'bg-gray-100 text-gray-600';
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-full px-2 text-[11px] font-medium',
        color,
      )}
      title={divergent ? 'Score manuel' : 'Score automatique'}
    >
      {divergent && '✱ '}
      {score}
    </span>
  );
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

function ListView({
  items,
  loading,
  onOpen,
}: {
  items: OpportunityListItem[];
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  const columns: Column<OpportunityListItem>[] = [
    {
      key: 'reference',
      header: 'Référence',
      render: (o) => (
        <div>
          <div className="font-mono text-xs text-text-muted">{o.reference}</div>
          <div className="mt-0.5 text-[11px] text-text-muted">{o.contractType.code}</div>
        </div>
      ),
    },
    {
      key: 'prospect',
      header: 'Prospect',
      // max-w-[280px] cohérent avec le design Stripe-like : assez large pour
      // la majorité des noms d'entreprise/particulier, tronqué avec ellipsis
      // + tooltip natif (title) au hover pour les cas longs.
      render: (o) => (
        <div
          className="max-w-[280px] truncate font-medium text-brand"
          title={displayName(o.prospect)}
        >
          {displayName(o.prospect)}
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Étape',
      // Badge `sm` (compact) + nowrap → colonne plus étroite, libère de la
      // place pour Prime HT / COM (cf demande 2026-06-01).
      className: 'whitespace-nowrap',
      render: (o) => <PipelineStageBadge stage={o.stage} size="sm" />,
    },
    {
      key: 'rdv',
      header: 'RDV',
      className: 'whitespace-nowrap',
      // Date du dernier RDV (vert = fait, rouge = à faire) pour les opps en
      // « RDV pris » / « RDV effectué ». « — » sinon.
      render: (o) =>
        o.lastMeeting ? (
          <RdvDateInline meeting={o.lastMeeting} />
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'premiumHt',
      header: 'Prime HT',
      align: 'right',
      // nowrap : « 120 000 € » ne doit jamais passer sur 2 lignes.
      className: 'whitespace-nowrap',
      render: (o) =>
        o.premiumHt ? (
          `${Number(o.premiumHt).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'commissionAmount',
      header: 'COM',
      align: 'right',
      className: 'whitespace-nowrap',
      render: (o) =>
        o.commissionAmount && Number(o.commissionAmount) > 0 ? (
          `${Number(o.commissionAmount).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'score',
      header: 'Score',
      render: (o) =>
        o.effectiveScore !== null ? (
          <ScoreBadge
            score={Math.round(Number(o.effectiveScore))}
            divergent={Boolean(o.manualScore)}
          />
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'assignedUser',
      header: 'Commercial',
      render: (o) => (
        <span>
          {o.assignedUser.firstName} {o.assignedUser.lastName}
        </span>
      ),
    },
    {
      key: 'lastActivityAt',
      header: 'Dernière activité',
      render: (o) => (
        <span className="text-xs text-text-muted">
          {o.lastActivityAt
            ? new Date(o.lastActivityAt).toLocaleDateString('fr-FR')
            : new Date(o.stageChangedAt).toLocaleDateString('fr-FR')}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      data={items}
      keyExtractor={(o) => o.id}
      loading={loading}
      onRowClick={(o) => onOpen(o.id)}
      empty={{
        title: 'Aucune opportunité',
        description: 'Créez une opportunité depuis une fiche prospect.',
      }}
    />
  );
}
